import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { getActiveHoliday, fmtHolidayDate } from '../../../lib/holiday';
import { sendFollowUpWA } from '../../../lib/fonnte';
import { buildMenuHppMap, buildPackageMap, resolveLineHpp, resolvePcs } from '../../../lib/hpp';

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const fmtRp  = n => 'Rp ' + (n || 0).toLocaleString('id-ID');
const fmtPct = n => `${n >= 0 ? '+' : ''}${n}%`;
const today  = () => new Date().toISOString().split('T')[0];
const daysSince = d => d ? Math.floor((Date.now() - new Date(d)) / 86400000) : 999;

function pctDelta(cur, prev) {
  if (!prev) return null;
  return Math.round((cur - prev) / prev * 100);
}

const S = {
  pad: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px)',
  card: { background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' },
  muted: { fontSize: '.76rem', color: 'var(--muted)' },
};

// ── Delta chip ────────────────────────────────────────────────────
function DeltaChip({ pct, higherGood = true }) {
  if (pct === null || pct === undefined) return null;
  const good = higherGood ? pct >= 0 : pct <= 0;
  const bg   = good ? '#E8F5E9' : '#FFEBEE';
  const c    = good ? '#1B5E20' : '#C62828';
  return (
    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.64rem', background: bg, color: c, borderRadius: 20, padding: '2px 8px', marginLeft: 6, whiteSpace: 'nowrap' }}>
      {pct > 0 ? '↑' : pct < 0 ? '↓' : '→'} {Math.abs(pct)}% vs kemarin
    </span>
  );
}

// ── KPI card ──────────────────────────────────────────────────────
function KPICard({ label, value, sub, delta, higherGood = true, accent = '#1C5BD4', accentBg = '#EFF6FF', urgent }) {
  return (
    <div style={{ ...S.card, padding: '16px 18px', borderLeft: urgent ? '3px solid #C62828' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <span style={{ fontSize: '.76rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', fontFamily: "'DM Mono',monospace" }}>{label}</span>
        <span style={{ width: 32, height: 32, borderRadius: 8, background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>
          {urgent ? '🔴' : ''}
        </span>
      </div>
      <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.6rem', color: urgent ? '#C62828' : 'var(--dark)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ ...S.muted, marginTop: 4 }}>{sub}</div>}
      <DeltaChip pct={delta} higherGood={higherGood} />
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────
function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: '.56rem', fontFamily: "'DM Mono',monospace", letterSpacing: '1.8px', textTransform: 'uppercase', color: 'rgba(201,146,42,.7)', marginBottom: 2 }}>{title}</div>
      {sub && <div style={{ fontSize: '.76rem', color: 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

// ── Insight banner ────────────────────────────────────────────────
function Insight({ text, level = 'info' }) {
  const map = { ok: ['#E8F5E9','#1B5E20'], warn: ['#FFF8E1','#E65100'], danger: ['#FFEBEE','#C62828'], info: ['#EFF6FF','#1C5BD4'] };
  const [bg, c] = map[level];
  return <div style={{ background: bg, color: c, borderRadius: 8, padding: '10px 14px', fontSize: '.8rem', lineHeight: 1.6 }}>{text}</div>;
}

// ─────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [ts, setTs]           = useState(null); // last refresh time

  const load = useCallback(async () => {
    setLoading(true);
    const todayStr = today();
    const yest     = new Date(); yest.setDate(yest.getDate() - 1);
    const yesterStr = yest.toISOString().split('T')[0];

    const [
      posToday, posYest,
      poUpcoming,
      customersRes,
      poOrdersRes,
      hourlyRes,
      menuItemsRes,
      packagesRes,
    ] = await Promise.all([
      // POS orders hari ini — order_items ditarik sekaligus lewat relasi FK
      // (JANGAN query tabel order_items terpisah dengan filter created_at:
      // tabel order_items TIDAK punya kolom created_at, query begitu akan
      // selalu gagal/null dan bikin Margin & Menu Terlaris kosong terus)
      supabase.from('pos_orders')
        .select('id,status,total,payment,type,created_at,staff_name,loyalty_discount,birthday_discount,customer_id, order_items(display_name,qty,unit_price,hpp_snapshot)')
        .gte('created_at', todayStr + 'T00:00:00')
        .lte('created_at', todayStr + 'T23:59:59'),

      // POS orders kemarin (untuk delta)
      supabase.from('pos_orders')
        .select('id,status,total')
        .gte('created_at', yesterStr + 'T00:00:00')
        .lte('created_at', yesterStr + 'T23:59:59'),

      // PO batch aktif (terdekat)
      supabase.from('po_batches')
        .select('id,date,time_start,time_end,min_packs,status, product:products(name,price), area:areas(name), po_orders(qty,status,payment_method,payment_status)')
        .not('status', 'in', '("selesai","batal")')
        .order('date', { ascending: true })
        .limit(5),

      // Customers untuk at-risk
      supabase.from('customers')
        .select('id,name,wa,visit_count,last_seen_at,created_at,loyalty_tier')
        .order('last_seen_at', { ascending: true })
        .limit(200),

      // PO orders untuk produk terakhir per customer
      supabase.from('po_orders')
        .select('customer_id, created_at, po:po_batches(product:products(name))')
        .neq('status', 'batal')
        .order('created_at', { ascending: false })
        .limit(500),

      // POS orders hari ini untuk heatmap jam
      supabase.from('pos_orders')
        .select('created_at')
        .gte('created_at', todayStr + 'T00:00:00')
        .lte('created_at', todayStr + 'T23:59:59')
        .eq('status', 'Selesai'),

      // Menu items + varian — untuk fallback HPP live & label produksi dapur
      supabase.from('menu_items')
        .select('id,name,emoji,hpp,base_price,menu_variants(id,name)'),

      // Paket — untuk konversi qty paket -> pcs asli & koreksi HPP paket
      supabase.from('packages')
        .select('id,name,price,package_items(qty,menu_items(hpp,base_price))'),
    ]);

    // ── POS hari ini ─────────────────────────────────────────────
    const pos      = posToday.data || [];
    const done     = pos.filter(o => o.status === 'Selesai');
    const batal    = pos.filter(o => o.status === 'Batal');
    const active   = pos.filter(o => !['Selesai','Batal'].includes(o.status));
    const revenue  = done.reduce((s,o) => s + o.total, 0);
    const aov      = done.length ? Math.round(revenue / done.length) : 0;
    const cancelPct = pos.length ? Math.round(batal.length / pos.length * 100) : 0;
    const loyDisc  = done.reduce((s,o) => s + (o.loyalty_discount||0) + (o.birthday_discount||0), 0);

    // HPP map (untuk koreksi HPP paket & fallback HPP item lama — lihat lib/hpp.js)
    const menuHppMap = buildMenuHppMap(menuItemsRes.data || []);
    const packageMap = buildPackageMap(packagesRes.data || []);

    // Margin hari ini — hanya dari order yang SUDAH SELESAI (konsisten
    // dengan kartu Revenue, yang juga cuma menjumlah order Selesai).
    // Order yang masih New/aktif belum tentu jadi/lunas, jadi belum
    // ikut dihitung sebagai pendapatan & margin.
    const doneItems = done.flatMap(o => o.order_items || []);
    let totalHPP = 0, totalRev = 0, estimatedHppCount = 0;
    doneItems.forEach(i => {
      const { hpp, isLive } = resolveLineHpp(i, packageMap, menuHppMap);
      if (hpp > 0) {
        if (isLive) estimatedHppCount++;
        totalHPP += hpp;
        totalRev += i.unit_price * i.qty;
      }
    });
    const marginAmt = totalRev - totalHPP;
    const marginPct = totalRev > 0 ? Math.round(marginAmt / totalRev * 100) : null;

    // Kebutuhan produksi dapur hari ini — dari SEMUA order hari ini yang
    // belum batal (termasuk yang masih berstatus New/aktif, bukan cuma
    // yang sudah Selesai), karena dapur perlu menyiapkan begitu order
    // masuk, tidak perlu menunggu order ditandai selesai. Paket otomatis
    // dipecah ke pcs asli komponennya lewat resolvePcs (lihat lib/hpp.js).
    const productionOrders = pos.filter(o => o.status !== 'Batal');
    const prodMap = {};
    productionOrders.forEach(o => {
      (o.order_items || []).forEach(i => {
        const name = i.display_name;
        if (!name) return;
        const pcs = resolvePcs(i, packageMap);
        if (!prodMap[name]) prodMap[name] = { name, emoji: '🍽️', qty: 0, newQty: 0 };
        prodMap[name].qty += pcs;
        if (o.status !== 'Selesai') prodMap[name].newQty += pcs;
      });
    });

    const productionList  = Object.values(prodMap).sort((a, b) => b.qty - a.qty);
    const totalProduction = productionList.reduce((s, p) => s + p.qty, 0);
    const totalProductionPending = productionList.reduce((s, p) => s + p.newQty, 0);

    // Kas tunai estimasi hari ini
    const kasHariIni = done.filter(o => ['Cash','Tunai'].includes(o.payment)).reduce((s,o) => s + o.total, 0);

    // Top menu hari ini (dari order yang sudah Selesai)
    const menuMap = {};
    doneItems.forEach(i => {
      const name = (i.display_name || '').split(' – ')[0].trim();
      menuMap[name] = (menuMap[name] || 0) + i.qty;
    });
    const topMenu = Object.entries(menuMap).sort((a,b) => b[1]-a[1]).slice(0,5);

    // ── Kemarin (delta) ───────────────────────────────────────────
    const yPOS     = posYest.data || [];
    const yDone    = yPOS.filter(o => o.status === 'Selesai');
    const yRev     = yDone.reduce((s,o) => s + o.total, 0);
    const yTotal   = yPOS.length;
    const yAOV     = yDone.length ? Math.round(yRev / yDone.length) : 0;

    // ── PO aktif ──────────────────────────────────────────────────
    const upcoming = poUpcoming.data || [];

    // ── Customer at-risk ──────────────────────────────────────────
    const lastProdMap = {};
    (poOrdersRes.data || []).forEach(o => {
      if (o.customer_id && !lastProdMap[o.customer_id])
        lastProdMap[o.customer_id] = o.po?.product?.name || 'HAPin';
    });
    const orderCntMap = {};
    (poOrdersRes.data || []).forEach(o => {
      if (o.customer_id) orderCntMap[o.customer_id] = (orderCntMap[o.customer_id]||0)+1;
    });

    const custs = customersRes.data || [];
    const atRisk = custs
      .filter(c => {
        const ref  = c.last_seen_at || c.created_at;
        const days = daysSince(ref);
        return (c.visit_count >= 1 || orderCntMap[c.id]) && days > 30;
      })
      .map(c => ({ ...c, lastProductName: lastProdMap[c.id] || 'HAPin', orderCount: orderCntMap[c.id] || 0 }));

    // ── Jam tersibuk hari ini ─────────────────────────────────────
    const hourMap = Array(24).fill(0);
    (hourlyRes.data || []).forEach(o => {
      const h = new Date(o.created_at).getHours();
      hourMap[h]++;
    });
    const peakHour = hourMap.indexOf(Math.max(...hourMap));

    setData({
      pos, done, batal, active,
      revenue, aov, cancelPct, loyDisc,
      marginAmt, marginPct, estimatedHppCount, kasHariIni,
      topMenu,
      productionList, totalProduction, totalProductionPending,
      yRev, yTotal, yAOV, yDone: yDone.length,
      deltaRev: pctDelta(revenue, yRev),
      deltaTotal: pctDelta(pos.length, yTotal),
      deltaAOV: pctDelta(aov, yAOV),
      upcoming, atRisk,
      totalCusts: custs.length,
      hourMap, peakHour,
    });
    setTs(new Date());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const [holiday, setHoliday] = useState(null);
  useEffect(() => { getActiveHoliday().then(setHoliday); }, []);

  async function handleFollowUp(c) {
    setSending(s => ({ ...s, [c.id]: true }));
    await sendFollowUpWA(c.wa, c.name, c.lastProductName);
    setSending(s => ({ ...s, [c.id]: false }));
  }

  const todayLabel = new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return (
    <div style={{ padding: S.pad, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: 'var(--muted)', fontSize: '.84rem' }}>
      Memuat data hari ini...
    </div>
  );

  const d = data;

  // ── Auto-generated insights ───────────────────────────────────
  const insights = [];
  if (d.active.length >= 5)
    insights.push({ text: `🔴 ${d.active.length} pesanan POS sedang aktif — pastikan staf siap.`, level: 'danger' });
  if (d.cancelPct > 10)
    insights.push({ text: `⚠️ Cancel rate ${d.cancelPct}% — ${d.batal.length} pesanan dibatalkan hari ini. Cek stok atau kendala pembayaran.`, level: 'warn' });
  if (d.marginPct !== null && d.marginPct < 20)
    insights.push({ text: `⚠️ Margin hari ini ${d.marginPct}% — di bawah target 30%. Dorong penjualan item ber-margin tinggi.`, level: 'warn' });
  if (d.revenue > d.yRev * 1.15 && d.yRev > 0)
    insights.push({ text: `✅ Revenue hari ini ${fmtRp(d.revenue)} — naik ${d.deltaRev}% dari kemarin. Pertahankan momentum!`, level: 'ok' });
  if (d.atRisk.length > 0)
    insights.push({ text: `📱 ${d.atRisk.length} customer tidak order lebih dari 30 hari. Kirim follow-up WA.`, level: 'warn' });
  if (d.cancelPct === 0 && d.pos.length >= 5)
    insights.push({ text: `✅ Nol pembatalan hari ini dari ${d.pos.length} pesanan. Operasional berjalan lancar!`, level: 'ok' });

  const marginColor = d.marginPct === null ? 'var(--muted)' : d.marginPct >= 30 ? '#1B5E20' : d.marginPct >= 15 ? '#E65100' : '#C62828';
  const cancelColor = d.cancelPct === 0 ? '#1B5E20' : d.cancelPct <= 10 ? '#E65100' : '#C62828';

  return (
    <div style={{ padding: S.pad, paddingBottom: 60 }}>
      <style>{`
        @media(max-width:640px){
          .dash-kpi { grid-template-columns: 1fr 1fr !important; }
          .dash-mid  { grid-template-columns: 1fr !important; }
          .dash-bot  { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Header ── */}
      {holiday && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FDEBE5', border: '1px solid rgba(205,82,38,.25)', borderRadius: 10, padding: '10px 16px', marginBottom: 14 }}>
          <span style={{ fontSize: '1.1rem' }}>🌴</span>
          <div style={{ fontSize: '.8rem', color: '#B23A15' }}>
            Toko disetel <strong>libur</strong> hari ini ({holiday.reason}) — web menampilkan halaman "Sedang Libur" dan order baru diblokir sampai {fmtHolidayDate(holiday.date_to)}.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem', color: 'var(--dark)' }}>Dashboard</div>
          <div style={{ ...S.muted, marginTop: 2 }}>{todayLabel}</div>
        </div>
        <button onClick={load} style={{ padding: '7px 14px', borderRadius: 8, background: 'white', border: '1px solid var(--border)', fontSize: '.78rem', color: 'var(--muted)', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
          ↺ Perbarui {ts && <span style={{ marginLeft: 4, color: '#CCC' }}>{ts.toLocaleTimeString('id-ID',{hour:'2-digit',minute:'2-digit'})}</span>}
        </button>
      </div>

      {/* ── Insights banner ── */}
      {insights.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '14px 0' }}>
          {insights.map((ins, i) => <Insight key={i} text={ins.text} level={ins.level} />)}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* BLOK 1 — Pulse Hari Ini (POS)                            */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionHeader title="Pulse Hari Ini — POS" sub="Data real-time transaksi kasir hari ini vs kemarin" />
      <div className="dash-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
        <KPICard
          label="Total Order"
          value={d.pos.length}
          sub={`${d.done.length} selesai · ${d.batal.length} batal · ${d.active.length} aktif`}
          delta={d.deltaTotal}
          accentBg="#EFF6FF"
        />
        <KPICard
          label="Revenue"
          value={fmtRp(d.revenue)}
          sub={`AOV: ${fmtRp(d.aov)}`}
          delta={d.deltaRev}
          accentBg="#ECFDF5"
        />
        <KPICard
          label="Margin Kotor"
          value={d.marginPct !== null ? `${d.marginPct}%` : '—'}
          sub={d.marginPct !== null
            ? `${fmtRp(d.marginAmt)} dari penjualan ber-HPP${d.estimatedHppCount > 0 ? ` · ${d.estimatedHppCount} item pakai HPP terkini` : ''}`
            : 'HPP belum diisi di menu'}
          accentBg={d.marginPct !== null && d.marginPct < 20 ? '#FFEBEE' : '#ECFDF5'}
          urgent={d.marginPct !== null && d.marginPct < 15}
        />
        <KPICard
          label="Cancel Rate"
          value={`${d.cancelPct}%`}
          sub={`${d.batal.length} pesanan dibatalkan`}
          delta={d.cancelPct > 0 ? pctDelta(d.cancelPct, 0) : null}
          higherGood={false}
          urgent={d.cancelPct > 10}
          accentBg={d.cancelPct === 0 ? '#ECFDF5' : '#FFEBEE'}
        />
        <KPICard
          label="Estimasi Kas Tunai"
          value={fmtRp(d.kasHariIni)}
          sub="Dari transaksi Cash/Tunai selesai"
          accentBg="#FFFBEB"
        />
        <KPICard
          label="Loyalty Discount"
          value={fmtRp(d.loyDisc)}
          sub="Total redeem poin & birthday hari ini"
          accentBg="#F5F3FF"
        />
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* BLOK 1b — Dapur: Perlu Diproduksi Hari Ini                */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionHeader title="Dapur — Perlu Diproduksi Hari Ini" sub="Total pcs per item dari seluruh order hari ini yang belum batal (termasuk order yang masih berstatus baru/aktif, bukan cuma yang sudah selesai) — paket otomatis dipecah ke satuan pcs" />
      <div style={{ ...S.card, padding: '18px 20px', marginBottom: 20 }}>
        {d.productionList.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: '.82rem' }}>Belum ada order masuk hari ini</div>
        ) : (
          <>
            {d.productionList.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F5EEE6' }}>
                <span style={{ fontSize: '.86rem' }}>
                  {p.emoji} <strong>{p.name}</strong>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {p.newQty > 0 && (
                    <span style={{ fontSize: '.66rem', fontFamily: "'DM Mono',monospace", color: '#B45309', background: '#FEF3C7', padding: '2px 7px', borderRadius: 999 }}>
                      +{p.newQty} order baru
                    </span>
                  )}
                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.88rem', fontWeight: 700, color: 'var(--dark)' }}>{p.qty} pcs</span>
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, marginTop: 4 }}>
              <span style={{ ...S.muted, fontFamily: "'DM Mono',monospace", fontSize: '.66rem', letterSpacing: '.8px', textTransform: 'uppercase' }}>
                Total Semua Item{d.totalProductionPending > 0 ? ` · ${d.totalProductionPending} pcs dari order baru` : ''}
              </span>
              <span style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--brown)' }}>{d.totalProduction} pcs</span>
            </div>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* BLOK 2 — Menu Terlaris Hari Ini + PO Aktif               */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="dash-mid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* Menu terlaris */}
        <div style={{ ...S.card, padding: '18px 20px' }}>
          <SectionHeader title="Menu Terlaris Hari Ini" />
          {d.topMenu.length === 0
            ? <div style={S.muted}>Belum ada penjualan hari ini</div>
            : d.topMenu.map(([name, qty], i) => {
              const COLORS = ['#1C5BD4','#1B5E20','#E65100','#7C3AED','#BE185D'];
              const maxQty = d.topMenu[0][1] || 1;
              return (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: COLORS[i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.68rem', color: 'white', fontWeight: 700, flexShrink: 0 }}>{i+1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.84rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                    <div style={{ height: 5, borderRadius: 50, background: '#EEE', marginTop: 3 }}>
                      <div style={{ height: '100%', borderRadius: 50, background: COLORS[i], width: `${Math.round(qty/maxQty*100)}%`, transition: 'width .5s' }} />
                    </div>
                  </div>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '.78rem', fontWeight: 700, color: COLORS[i], flexShrink: 0 }}>{qty}x</div>
                </div>
              );
            })
          }
        </div>

        {/* PO aktif */}
        <div style={{ ...S.card, padding: '18px 20px' }}>
          <SectionHeader title="PO Aktif" sub="Status batch pre-order yang sedang berjalan" />
          {d.upcoming.length === 0
            ? <div style={S.muted}>Tidak ada PO aktif saat ini</div>
            : d.upcoming.map(po => {
              const total = (po.po_orders || []).filter(o => o.status !== 'batal' && (o.payment_method === 'cod' || (o.payment_method === 'non-tunai' && o.payment_status === 'paid'))).reduce((s,o) => s+o.qty, 0);
              const pct   = Math.min(100, Math.round((total / (po.min_packs||1)) * 100));
              const STATUS_LABEL = { open:'Buka', closed:'Ditutup', diproses:'Diproses', dikirim:'Dikirim', ditempat:'Di Lokasi' };
              const statusLabel  = STATUS_LABEL[po.status] || po.status;
              const isWarning    = po.status === 'open' && pct < 50 && (() => { const d2 = new Date(po.date); d2.setDate(d2.getDate()-1); return new Date() >= d2; })();
              return (
                <div key={po.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                    <div>
                      <strong style={{ fontSize: '.86rem' }}>{po.product?.name}</strong>
                      <span style={{ ...S.muted, marginLeft: 6 }}>· {po.area?.name}</span>
                    </div>
                    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.62rem', background: po.status === 'open' ? '#E8F5E9' : '#EFF6FF', color: po.status === 'open' ? '#1B5E20' : '#1C5BD4', borderRadius: 20, padding: '2px 8px' }}>{statusLabel}</span>
                  </div>
                  <div style={{ ...S.muted, marginBottom: 5 }}>
                    {new Date(po.date).toLocaleDateString('id-ID',{day:'numeric',month:'short'})} · {po.time_start?.slice(0,5)}–{po.time_end?.slice(0,5)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', ...S.muted, marginBottom: 4 }}>
                    <span>{total} / {po.min_packs} pack</span>
                    <span style={{ color: pct >= 100 ? '#1B5E20' : isWarning ? '#C62828' : 'var(--muted)' }}>{pct}%{isWarning ? ' ⚠' : ''}</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 50, background: '#EEE' }}>
                    <div style={{ height: '100%', borderRadius: 50, background: pct >= 100 ? '#27AE60' : isWarning ? '#C62828' : 'var(--gold)', width: pct+'%', transition: 'width .5s' }} />
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* BLOK 2b — Jam Tersibuk Hari Ini                           */}
      {/* ══════════════════════════════════════════════════════════ */}
      <SectionHeader title="Jam Tersibuk Hari Ini" sub="Jumlah order selesai per jam — perbarui halaman untuk data terbaru" />
      <div style={{ ...S.card, padding: '18px 20px', marginBottom: 20 }}>
        {d.done.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: '.82rem' }}>Belum ada transaksi selesai hari ini</div>
        ) : (
          <>
            {/* Bar chart 24 jam */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 56, marginBottom: 6 }}>
              {d.hourMap.map((cnt, h) => {
                const maxCnt = Math.max(...d.hourMap, 1);
                const barH   = cnt > 0 ? Math.max(4, Math.round(cnt / maxCnt * 56)) : 2;
                const isPeak = h === d.peakHour && cnt > 0;
                const isNow  = h === new Date().getHours();
                return (
                  <div key={h} title={`${String(h).padStart(2,'0')}:00 — ${cnt} order`}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
                    <div style={{
                      width: '85%', height: barH,
                      background: isPeak ? 'var(--dark)' : isNow ? 'var(--gold)' : cnt > 0 ? '#C9922A44' : '#EEE',
                      borderRadius: '3px 3px 0 0', transition: 'height .4s',
                      outline: isPeak ? '2px solid var(--gold)' : 'none',
                    }} />
                  </div>
                );
              })}
            </div>
            {/* X labels — tiap 3 jam */}
            <div style={{ display: 'flex', gap: 2, marginBottom: 10 }}>
              {d.hourMap.map((_, h) => (
                <div key={h} style={{ flex: 1, textAlign: 'center', fontSize: '.52rem', color: 'var(--muted)', fontFamily: "'DM Mono',monospace" }}>
                  {h % 3 === 0 ? String(h).padStart(2,'0') : ''}
                </div>
              ))}
            </div>
            {/* Legend + insight */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: '.78rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--dark)', outline: '1.5px solid var(--gold)' }} />
                <span>Jam puncak: <strong>{String(d.peakHour).padStart(2,'0')}:00–{String(d.peakHour+1).padStart(2,'0')}:00</strong> ({d.hourMap[d.peakHour]} order)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--gold)' }} />
                <span>Jam sekarang</span>
              </div>
              {d.hourMap[d.peakHour] > 0 && (
                <span style={{ marginLeft: 'auto', fontSize: '.74rem', color: 'var(--muted)' }}>
                  Total {d.done.length} order selesai hari ini
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* BLOK 3 — Customer Follow-up + Ringkasan Kasir             */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="dash-bot" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* Ringkasan kasir kemarin */}
        <div style={{ ...S.card, padding: '18px 20px' }}>
          <SectionHeader title="Kemarin vs Hari Ini" sub="Perbandingan cepat performa harian" />
          {[
            ['Order', d.pos.length, d.yTotal, true],
            ['Order Selesai', d.done.length, d.yDone, true],
            ['Revenue', fmtRp(d.revenue), fmtRp(d.yRev), true, pctDelta(d.revenue, d.yRev)],
            ['AOV', fmtRp(d.aov), fmtRp(d.yAOV), true, pctDelta(d.aov, d.yAOV)],
          ].map(([label, cur, prev, hiGood, delta]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F5EEE6' }}>
              <span style={S.muted}>{label}</span>
              <div style={{ textAlign: 'right' }}>
                <strong style={{ fontSize: '.88rem' }}>{cur}</strong>
                <span style={{ ...S.muted, marginLeft: 8 }}>← {prev}</span>
                {delta !== undefined && <DeltaChip pct={delta} higherGood={hiGood} />}
              </div>
            </div>
          ))}
        </div>

        {/* Customer at-risk */}
        <div style={{ ...S.card, padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <SectionHeader title="Customer Perlu Follow-up" />
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.66rem', background: d.atRisk.length > 0 ? '#FFEBEE' : '#E8F5E9', color: d.atRisk.length > 0 ? '#C62828' : '#1B5E20', borderRadius: 20, padding: '2px 8px' }}>{d.atRisk.length}</span>
          </div>
          <div style={{ ...S.muted, marginBottom: 12 }}>Tidak order &gt; 30 hari · {d.totalCusts} total customer</div>
          {d.atRisk.length === 0
            ? <div style={{ color: '#1B5E20', fontSize: '.82rem' }}>Semua customer aktif 👍</div>
            : d.atRisk.slice(0, 5).map(c => {
              const ref  = c.last_seen_at || c.created_at;
              const days = daysSince(ref);
              return (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #F5EEE6' }}>
                  <div>
                    <div style={{ fontSize: '.84rem', fontWeight: 500 }}>{c.name}</div>
                    <div style={S.muted}>{days} hari lalu · {c.orderCount || c.visit_count}x order</div>
                  </div>
                  <button onClick={() => handleFollowUp(c)} disabled={sending[c.id]}
                    style={{ padding: '5px 10px', borderRadius: 6, background: sending[c.id] ? '#EEE' : '#E8F5E9', border: '1px solid #A5D6A7', color: sending[c.id] ? '#999' : '#1B5E20', fontSize: '.72rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {sending[c.id] ? '...' : '📱 Follow-up'}
                  </button>
                </div>
              );
            })
          }
          {d.atRisk.length > 5 && <div style={{ ...S.muted, marginTop: 8 }}>+ {d.atRisk.length - 5} customer lainnya →</div>}
        </div>
      </div>

    </div>
  );
}
